// Updated BillHistory.jsx
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { parseISO, format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import CardComponent from "../../components/cards/CardComponent";
import DataTable from "../../components/data-tables/dataTable";
import fetchData from "../../functions/fetchData";
import logo from "../../assets/logo.png";
import BillItemModal from "../../components/Modals/BillItemModal";
import { getUserType } from "../../utility/auth";
import { FaHome, FaArrowLeft } from "react-icons/fa";

export default function BillHistory() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [startDate, setStartDate] = useState(localStorage.getItem("startDate") || "");
  const [endDate, setEndDate] = useState(localStorage.getItem("endDate") || "");
  const [paymentMode, setPaymentMode] = useState(localStorage.getItem("paymentMode") || "");
  const [orderNoSearch, setOrderNoSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [cancelledTotal, setCancelledTotal] = useState(0);
  const [tableFilter, setTableFilter] = useState("");
  const [amountFilter, setAmountFilter] = useState({ min: "", max: "" });
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  
  const navigate = useNavigate();
  const userType = getUserType();
  const isCashier = userType?.toLowerCase() === 'cashier';

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        await fetchData("final_bill", setData, "id",{});
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchAllData();
  }, []);

  // Initial data load effect
  useEffect(() => {
    if (data.length > 0) {
      applyFilters();
    }
  }, [data]);

  // Filter change effect
  useEffect(() => {
    localStorage.setItem("startDate", startDate);
    localStorage.setItem("endDate", endDate);
    localStorage.setItem("paymentMode", paymentMode);
    if (data.length > 0) {
      applyFilters();
    }
  }, [startDate, endDate, paymentMode, activeTab, orderNoSearch, tableFilter, amountFilter, sortBy, sortOrder]);

  const applyFilters = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error("Invalid date range: Start Date is after End Date");
      return;
    }

    let filtered = [...data];
    
    // Debug: Log raw data status values
    console.log("Raw data status values:", data.map(item => ({ id: item.id, status: item.status, type: typeof item.status })));
    console.log("Total raw data count:", data.length);
    
    // TEMPORARY: Show all data first to debug
    console.log("Active tab:", activeTab);
    console.log("All data:", data);
    
    // Filter by status (active/cancelled)
    if (activeTab === "cancelled") {
      filtered = filtered.filter(item => item.status === 2 || item.status === "2");
      console.log("Filtered cancelled bills:", filtered.length);
    } else {
      filtered = filtered.filter(item => item.status !== 2 && item.status !== "2");
      console.log("Filtered active bills:", filtered.length);
    }
    
    // TEMPORARY: If no filtered data, show all data for debugging
    if (filtered.length === 0) {
      console.log("No filtered data found, showing all data for debugging");
      filtered = [...data];
    }
    
    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Include the entire end date
      
      filtered = filtered.filter(item => {
        if (!item.setup_date) return false;
        const itemDate = new Date(item.setup_date);
        return itemDate >= start && itemDate <= end;
      });
    } else if (startDate) {
      const start = new Date(startDate);
      filtered = filtered.filter(item => {
        if (!item.setup_date) return false;
        const itemDate = new Date(item.setup_date);
        return itemDate >= start;
      });
    } else if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => {
        if (!item.setup_date) return false;
        const itemDate = new Date(item.setup_date);
        return itemDate <= end;
      });
    }
    
    // Filter by payment mode
    if (paymentMode) {
      filtered = filtered.filter(item => item.payment_mode === paymentMode);
    }
    
    // Filter by invoice number
    if (orderNoSearch) {
      filtered = filtered.filter(item => 
        item.id.toString().includes(orderNoSearch.toLowerCase())
      );
    }
    
    // Filter by table
    if (tableFilter) {
      filtered = filtered.filter(item => 
        item.table_number && item.table_number.toLowerCase().includes(tableFilter.toLowerCase())
      );
    }
    
    // Filter by amount range
    if (amountFilter.min || amountFilter.max) {
      filtered = filtered.filter(item => {
        const amount = parseFloat(item.grand_total || 0);
        const minAmount = parseFloat(amountFilter.min || 0);
        const maxAmount = parseFloat(amountFilter.max || Infinity);
        return amount >= minAmount && amount <= maxAmount;
      });
    }
    
    // Sort data
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case "date":
          aValue = new Date(a.setup_date + " " + (a.inv_time || "00:00:00"));
          bValue = new Date(b.setup_date + " " + (b.inv_time || "00:00:00"));
          break;
        case "amount":
          aValue = parseFloat(a.grand_total || 0);
          bValue = parseFloat(b.grand_total || 0);
          break;
        case "table":
          aValue = a.table_number || "";
          bValue = b.table_number || "";
          break;
        case "invoice":
          aValue = parseInt(a.id || 0);
          bValue = parseInt(b.id || 0);
          break;
        default:
          aValue = a.id;
          bValue = b.id;
      }
      
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredData(filtered);
    
    // Calculate totals
    if (activeTab === "cancelled") {
      const total = filtered.reduce((acc, item) => acc + parseFloat(item.grand_total || 0), 0);
      setCancelledTotal(total.toFixed(2));
    }
  };
  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setPaymentMode("");
    setOrderNoSearch("");
    setTableFilter("");
    setAmountFilter({ min: "", max: "" });
    setSortBy("date");
    setSortOrder("desc");
    localStorage.removeItem("startDate");
    localStorage.removeItem("endDate");
    localStorage.removeItem("paymentMode");
  };

 const exportPDF = () => {
  const doc = new jsPDF();
  doc.addImage(logo, "PNG", 150, 10, 40, 15);
  doc.setFontSize(16);
  doc.text("Bill History", 14, 20);

  const tableColumn = ["Invoice No", "Date", "Time", "Subtotal", "Grand Total", "Payment Mode"];
  const tableRows = [];

  let totalSubtotal = 0;
  let totalGrandTotal = 0;

  filteredData.forEach(item => {
    const subtotal = parseFloat(item.subtotal_afterdiscount) || 0;
    const grandTotal = parseFloat(item.grand_total) || 0;

    totalSubtotal += subtotal;
    totalGrandTotal += grandTotal;

    tableRows.push([
      item.id,
      item.setup_date,
      item.inv_time,
      subtotal.toFixed(2),
      grandTotal.toFixed(2),
      item.payment_mode,
    ]);
  });

  // Add total row
  tableRows.push([
    { content: "Total", colSpan: 3, styles: { halign: "right", fontStyle: "bold" } },
    totalSubtotal.toFixed(2),
    totalGrandTotal.toFixed(2),
    "" // Empty payment mode column
  ]);

  doc.autoTable({
    startY: 30,
    head: [tableColumn],
    body: tableRows,
    styles: { fontSize: 10 },
  });

  doc.save("salereport.pdf");
};


  const handleViewItems = (row) => {
    setSelectedBill(row);
    setShowModal(true);
  };

  // New function to print VAT invoice with full format from POS
  const printBillVATFormat = async (billData) => {
    try {
      // Fetch order items for the bill
      const itemsResponse = await fetchData("order_items", () => {}, "order_id", { order_id: billData.id });
      
      // Fetch company profile if available
      let companyProfile = {};
      try {
        const companyResponse = await fetchData("company_profile", () => {}, "id", {});
        if (companyResponse && companyResponse.length > 0) {
          companyProfile = companyResponse[0];
        }
      } catch (error) {
        console.log("Using default company info", error);
      }

      // Fetch core settings to check showVat setting
      let showVatDetails = false;
      try {
        const coreSettingResponse = await fetchData("coresetting", () => {}, "id", {});
        if (coreSettingResponse && coreSettingResponse.length > 0) {
          showVatDetails = coreSettingResponse[0].showVat?.toLowerCase() === 'yes';
        }
      } catch (error) {
        console.log("Could not fetch core settings, defaulting showVat to false", error);
      }

      const invoiceData = {
        invoice_number: billData.id,
        setup_date: billData.setup_date,
        inv_time: billData.inv_time,
        payment_method: billData.payment_mode,
        customer_name: billData.customer_name || 'Walk-in Customer',
        customer_phone: billData.customer_phone || 'N/A',
        customer_email: billData.customer_email || '',
        customer_address: billData.customer_address || '',
        customer_gst: billData.customer_gst || '',
        items: itemsResponse.map(item => ({
          item_name: item.item_name,
          description: item.description || '',
          rate: item.rate || 0,
          quantity: item.quantity,
          discount_percent: item.discount_percent || 0,
          vat: item.vat || 7,
          total_amount: item.total_price,
          offerprice: item.rate || 0,
          iname: item.item_name
        })),
        subtotal: billData.subtotal,
        discount_value: billData.discount_value || 0,
        tax: billData.tax,
        round_off: billData.round_off || 0,
        grand_total: billData.grand_total,
        total: billData.subtotal
      };

      // Helper function to convert blob to base64
      const blobToBase64 = (blob, type) => {
        if (!blob) return null;
        const uint8Array = new Uint8Array(blob.data || blob);
        const binary = uint8Array.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        return `data:${type};base64,${btoa(binary)}`;
      };

      // Company details with fallback
      const companyName = companyProfile?.name || 'CloudNet Softwares';
      const companyPhone = companyProfile?.phone_number || '+66-948712350';
      const companyEmail = companyProfile?.email || 'info@cloudnetsoftwares.com';
      const companyWebsite = companyProfile?.website || 'www.cloudnetsoftwares.com';
      const companyAddress = companyProfile?.address || '123 Beach Road, Pattaya, Thailand';
      const companyCity = companyProfile?.city || '';
      const companyState = companyProfile?.state || '';
      const companyZip = companyProfile?.zip_code || '';
      const companyCountry = companyProfile?.country || '';
      const companyTaxId = companyProfile?.tax_id || '';
      const fullAddress = [companyAddress, companyCity, companyState, companyZip, companyCountry].filter(Boolean).join(', ');
      
      // Get logo if available
      const logoUrl = companyProfile?.logo && companyProfile?.logo_type 
        ? blobToBase64(companyProfile.logo, companyProfile.logo_type) 
        : null;

      // Get terms and conditions
      const termsAndConditions = companyProfile?.terms_and_conditions || `Terms & Conditions:
• All prices are inclusive of VAT
• Payment is due upon receipt
• Goods once sold cannot be returned`;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>VAT Invoice - ${invoiceData.invoice_number}</title>
          <style>
            @page { size: A4; margin: 8mm; }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 8px;
              background: white;
              color: #333;
            }
            .vat-invoice-container {
              max-width: 210mm;
              min-height: 270mm;
              margin: 0 auto;
              border: 2px solid #000;
              padding: 10px;
              display: flex;
              flex-direction: column;
            }
            .header-section {
              border-bottom: 3px solid #FF7F7F;
              padding-bottom: 6px;
              margin-bottom: 8px;
            }
            .company-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .company-logo-section {
              flex: 1;
            }
            .company-name {
              font-size: 13px;
              font-weight: bold;
              color: #050505ff;
              margin-bottom: 2px;
            }
            .company-tagline {
              font-size: 9px;
              color: #333;
              font-style: italic;
            }
            .company-contact {
              text-align: right;
              font-size: 14px;
              color: #0e0d0dff;
              line-height: 1.3;
            }
            .invoice-title {
              text-align: center;
              background: linear-gradient(135deg, #FF8A80 0%, #eb730a 100%);
              color: black;
              padding: 6px;
              margin: 6px 0;
              font-size: 16px;
              font-weight: bold;
              letter-spacing: 1px;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
              margin: 6px 0;
            }
            .info-box {
              border: 1px solid #e0e0e0;
              padding: 6px;
              border-radius: 5px;
              background: #f8f9fa;
            }
            .info-box-title {
              font-weight: bold;
              font-size: 11px;
              color: #000;
              border-bottom: 2px solid #FF8A80;
              padding-bottom: 3px;
              margin-bottom: 4px;
            }
            .info-row {
              display: flex;
              padding: 2px 0;
              font-size: 9px;
              line-height: 1.2;
            }
            .info-label {
              font-weight: 600;
              width: 90px;
              color: #555;
            }
            .info-value {
              flex: 1;
              color: #333;
            }
            .items-wrapper {
              flex: 1;
              display: flex;
              flex-direction: column;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin: 6px 0;
              font-size: 9px;
              box-shadow: 0 1px 2px rgba(0,0,0,0.1);
            }
            .items-table thead {
              background: linear-gradient(135deg, #b8b8b8ff 0%, #b8b8b8ff 100%);
              color: black;
            }
            .items-table th {
              padding: 5px 4px;
              text-align: left;
              font-weight: 600;
              border-right: 1px solid rgba(255,255,255,0.2);
            }
            .items-table th:last-child {
              border-right: none;
            }
            .items-table tbody tr {
              border-bottom: 1px solid #e0e0e0;
            }
            .items-table tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            .items-table tbody tr:hover {
              background-color: #e3f2fd;
            }
            .items-table td {
              padding: 4px 4px;
              vertical-align: middle;
            }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .summary-section {
              margin-top: auto;
              display: flex;
              justify-content: flex-end;
            }
            .summary-box {
              width: 280px;
              border: 2px solid #000;
              border-radius: 5px;
              overflow: hidden;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 4px 10px;
              border-bottom: 1px solid #e0e0e0;
              font-size: 10px;
            }
            .summary-row:last-child {
              border-bottom: none;
            }
            .summary-label {
              font-weight: 600;
              color: #555;
            }
            .summary-value {
              font-weight: 500;
              color: #333;
            }
            .vat-row {
              background-color: #FFE5E5;
            }
            .total-row {
              background: linear-gradient(135deg, #FF8A80 0%, #eb730a 100%);
              color: white;
              font-size: 12px;
              font-weight: bold;
            }
            .footer-section {
              margin-top: 10px;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              padding-top: 8px;
              border-top: 2px solid #FF8A80;
            }
            .terms-section {
              flex: 1;
              font-size: 8px;
              color: #666;
              line-height: 1.2;
            }
            .terms-title {
              font-weight: bold;
              margin-bottom: 3px;
              color: #FF8A80;
              font-size: 9px;
            }
            .signature-box {
              width: 160px;
              text-align: center;
            }
            .signature-line {
              border-top: 2px solid #000;
              margin-top: 30px;
              padding-top: 4px;
              font-weight: 600;
              color: #000;
              font-size: 9px;
            }
            .vat-badge {
              display: inline-block;
              background: #FF8A80;
              color: white;
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 9px;
              font-weight: bold;
              margin-left: 8px;
            }
            .watermark {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-45deg);
              font-size: 120px;
              font-weight: bold;
              color: rgba(0, 0, 0, 0.08);
              z-index: 1;
              pointer-events: none;
              text-transform: uppercase;
              letter-spacing: 10px;
            }
            .vat-invoice-container {
              position: relative;
            }
            @media print {
              body { margin: 0; padding: 10mm; }
              .vat-invoice-container { border: none; }
              @page { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="vat-invoice-container">
            <div class="watermark">COPY</div>
            <div class="header-section">
              <div class="company-header">
                <div class="company-logo-section">
                  ${logoUrl ? `
                    <img src="${logoUrl}" alt="${companyName} Logo" style="max-width: 140px; max-height: 60px; margin-bottom: 6px;" onerror="this.style.display='none'" />
                  ` : ''}
                  <div class="company-name">${companyName}</div>
                  ${companyTaxId ? `<div class="company-tagline">Tax ID: ${companyTaxId}</div>` : ''}
                </div>
                <div class="company-contact">
                  <div>📞 ${companyPhone}</div>
                  <div>🌐 ${companyWebsite}</div>
                  <div>📧 ${companyEmail}</div>
                  <div style="margin-top: 8px;">📍 ${fullAddress}</div>
                </div>
              </div>
            </div>

            <div class="invoice-title">
              TAX INVOICE
              
            </div>

            <div class="info-grid">
              <div class="info-box">
                <div class="info-box-title">📄 Invoice Information</div>
                <div class="info-row">
                  <span class="info-label">Invoice Number:</span>
                  <span class="info-value"><strong>${invoiceData.invoice_number}</strong></span>
                </div>
                <div class="info-row">
                  <span class="info-label">Invoice Date:</span>
                  <span class="info-value">${new Date(invoiceData.setup_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Invoice Time:</span>
                  <span class="info-value">${invoiceData.inv_time || new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Payment Method:</span>
                  <span class="info-value"><strong>${invoiceData.payment_method || 'Cash'}</strong></span>
                </div>
              </div>

              <div class="info-box">
                <div class="info-box-title">👤 Customer Details</div>
                <div class="info-row">
                  <span class="info-label">Name:</span>
                  <span class="info-value"><strong>${invoiceData.customer_name}</strong></span>
                </div>
                <div class="info-row">
                  <span class="info-label">Phone:</span>
                  <span class="info-value">${invoiceData.customer_phone}</span>
                </div>
                ${invoiceData.customer_email ? `
                <div class="info-row">
                  <span class="info-label">Email:</span>
                  <span class="info-value">${invoiceData.customer_email}</span>
                </div>` : ''}
                ${invoiceData.customer_address ? `
                <div class="info-row">
                  <span class="info-label">Address:</span>
                  <span class="info-value">${invoiceData.customer_address}</span>
                </div>` : ''}
                ${invoiceData.customer_gst ? `
                <div class="info-row">
                  <span class="info-label">Tax ID:</span>
                  <span class="info-value">${invoiceData.customer_gst}</span>
                </div>` : ''}
              </div>
            </div>

            <div class="items-wrapper">
              <table class="items-table">
                <thead>
                  <tr>
                    <th width="5%" class="text-center">#</th>
                    <th width="${showVatDetails ? '30' : '35'}%">Item Description</th>
                    <th width="12%" class="text-right">Unit Price</th>
                    <th width="8%" class="text-center">Qty</th>
                    <th width="10%" class="text-right">Amount</th>
                    <th width="10%" class="text-right">Disc%</th>
                    ${showVatDetails ? '<th width="10%" class="text-right">VAT%</th>' : ''}
                    <th width="15%" class="text-right">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoiceData.items && invoiceData.items.length > 0 ? invoiceData.items.map((item, index) => `
                    <tr>
                      <td class="text-center">${index + 1}</td>
                      <td><strong>${item.item_name || item.iname || 'N/A'}</strong>${item.description ? `<br><i style="font-size: 10px; color: #666;">${item.description}</i>` : ''}</td>
                      <td class="text-right">฿ ${(parseFloat(item.rate || item.offerprice || 0)).toFixed(2)}</td>
                      <td class="text-center">${item.quantity || 0}</td>
                      <td class="text-right">฿ ${(parseFloat(item.rate || item.offerprice || 0) * parseFloat(item.quantity || 1)).toFixed(2)}</td>
                      <td class="text-right">${parseFloat(item.discount_percent || 0).toFixed(0)}%</td>
                      ${showVatDetails ? `<td class="text-right">${parseFloat(item.vat || 7).toFixed(0)}%</td>` : ''}
                      <td class="text-right"><strong>฿ ${(parseFloat(item.total_amount || 0)).toFixed(2)}</strong></td>
                    </tr>
                  `).join('') : `<tr><td colspan="${showVatDetails ? '8' : '7'}" class="text-center">No items found</td></tr>`}
                </tbody>
              </table>
            </div>

            <hr style="border: none; border-top: 3px solid #000; margin: 15px 0;" />

            <div class="summary-section">
              <div class="summary-box">
                <div class="summary-row">
                  <span class="summary-label">Subtotal:</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.subtotal || invoiceData.total || 0).toFixed(2)}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Discount:</span>
                  <span class="summary-value">- ฿ ${parseFloat(invoiceData.discount_value || 0).toFixed(2)}</span>
                </div>
                ${showVatDetails ? `
                <div class="summary-row">
                  <span class="summary-label">Subtotal After Discount:</span>
                  <span class="summary-value">฿ ${(parseFloat(invoiceData.subtotal || 0) - parseFloat(invoiceData.discount_value || 0)).toFixed(2)}</span>
                </div>
                <div class="summary-row vat-row">
                  <span class="summary-label">🔖 VAT (7%):</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.tax || 0).toFixed(2)}</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Round Off:</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.round_off || 0).toFixed(2)}</span>
                </div>
                ` : ''}
                <div class="summary-row total-row">
                  <span class="summary-label">GRAND TOTAL:</span>
                  <span class="summary-value">฿ ${parseFloat(invoiceData.grand_total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div class="footer-section">
              <div class="terms-section">
                <div class="terms-title">Terms & Conditions:</div>
                ${termsAndConditions.split('\n').map(line => `<div>${line}</div>`).join('')}
                <div style="margin-top: 15px; font-size: 10px; font-style: italic;">
                  This is a computer-generated invoice and does not require a physical signature.
                </div>
              </div>
              ${(companyProfile?.bank_name || companyProfile?.account_number) ? `
              <div class="bank-details-box" style="width: 250px; border: 1px solid #e0e0e0; padding: 8px; border-radius: 5px; background: #f8f9fa; margin: 0 15px;">
                <div style="font-weight: bold; font-size: 10px; color: #000; border-bottom: 2px solid #FF8A80; padding-bottom: 3px; margin-bottom: 4px;">
                  🏦 Bank Details
                </div>
                ${companyProfile?.bank_name ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Bank Name:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.bank_name}</span>
                </div>` : ''}
                ${companyProfile?.account_name ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Account Name:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.account_name}</span>
                </div>` : ''}
                ${companyProfile?.account_number ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Account Number:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.account_number}</span>
                </div>` : ''}
                ${companyProfile?.routing_number ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">Routing Number:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.routing_number}</span>
                </div>` : ''}
                ${companyProfile?.swift_code ? `
                <div style="font-size: 8px; padding: 2px 0;">
                  <span style="font-weight: 600; color: #555;">SWIFT Code:</span>
                  <span style="color: #333; margin-left: 5px;">${companyProfile.swift_code}</span>
                </div>` : ''}
              </div>
              ` : ''}
              <div class="signature-box">
                <div class="signature-line">
                  Authorized Signatory
                </div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } catch (error) {
      toast.error('Error printing invoice');
      console.error('Print error:', error);
    }
  };

  const printVATInvoice = printBillVATFormat; // Legacy function name for compatibility

  const monthlyData = (() => {
    const summary = {};
    (filteredData.length > 0 ? filteredData : data).forEach((item) => {
      const month = format(parseISO(item.setup_date), "MMM yyyy");
      const total = parseFloat(item.grand_total || 0);
      summary[month] = (summary[month] || 0) + total;
    });
    return Object.entries(summary).map(([month, total]) => ({ month, total }));
  })();

  const columns = [
    {
      label: "Inv. No.",
      field: "id",
      render: (row) => (
        <button className="btn btn-link p-0" onClick={() => handleViewItems(row)}>
          #{row.id}
        </button>
      ),
    },
    { label: "Inv Date", field: "inv_date" },
    { label: "Setup Date", field: "setup_date" },
    { label: "Time", field: "inv_time" },
    { label: "Subtotal", field: "subtotal_afterdiscount" },
    { label: "Tax", field: "tax" },
    { label: "Grand Total", field: "grand_total" },
    { label: "Payment", field: "payment_mode" },
    {
      label: "Action",
      field: "custom_actions",
      render: (row) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
          <button className="btn btn-sm btn-info" onClick={() => handleViewItems(row)} style={{ whiteSpace: 'nowrap' }}>
            View Items
          </button>
          <button className="btn btn-sm btn-success" onClick={() => printVATInvoice(row)} title="Print A4 VAT Format" style={{ whiteSpace: 'nowrap' }}>
            🖨️ Print
          </button>
        </div>
      ),
    },
  ];

  // Navigation functions for cashiers
  const navigateToDashboard = () => {
    navigate('/dashboard');
  };

  const navigateBack = () => {
    navigate(-1);
  };

  // Check if mobile device
  const isMobile = window.innerWidth <= 768;

  // Render content without Layout/Header for cashiers
  const renderContent = () => (
    <>
      <ToastContainer />
      
      {/* Cashier Navigation Header */}
      {isCashier && (
        <div style={{
          backgroundColor: '#343a40',
          color: 'white',
          padding: isMobile ? '10px 15px' : '15px 20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          marginBottom: '20px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? '10px' : '15px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: isMobile ? '10px' : '15px',
              flexWrap: 'wrap'
            }}>
              <button 
                className="btn btn-outline-light btn-sm"
                onClick={navigateBack}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '5px',
                  borderRadius: '6px',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '5px 10px' : '8px 12px'
                }}
              >
                <FaArrowLeft size={isMobile ? 12 : 14} />
                Back
              </button>
              <h4 style={{ 
                margin: 0, 
                fontWeight: 'bold',
                fontSize: isMobile ? '1.2rem' : '1.5rem'
              }}>
                Sales Reports
              </h4>
            </div>
            <button 
              className="btn btn-outline-light btn-sm"
              onClick={navigateToDashboard}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '5px',
                borderRadius: '6px',
                fontSize: isMobile ? '12px' : '14px',
                padding: isMobile ? '5px 10px' : '8px 12px'
              }}
            >
              <FaHome size={isMobile ? 12 : 14} />
              Dashboard
            </button>
          </div>
        </div>
      )}

      <CardComponent>
        <style>{`
          .filter-card {
            background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.07);
          }
          .filter-input-sm {
            height: 32px;
            font-size: 13px;
            padding: 4px 10px;
            border-radius: 6px;
            border: 1px solid #d1d5db;
            transition: all 0.2s;
          }
          .filter-input-sm:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            outline: none;
          }
          .filter-label {
            font-size: 11px;
            font-weight: 600;
            color: #4b5563;
            margin-bottom: 4px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .tab-btn {
            padding: 6px 16px;
            font-size: 13px;
            font-weight: 600;
            border-radius: 8px;
            transition: all 0.3s;
          }
          .compact-btn {
            height: 32px;
            font-size: 12px;
            padding: 0 14px;
            border-radius: 6px;
            font-weight: 500;
            transition: all 0.2s;
          }
          .stats-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 10px 16px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
          }
        `}</style>
        
        <div className="filter-card">
          {/* Tab Buttons */}
          <div className="d-flex mb-3" style={{ gap: '10px' }}>
            <button
              className={`tab-btn btn btn-${activeTab === "active" ? "primary" : "outline-primary"}`}
              onClick={() => setActiveTab("active")}
            >
              <i className="fas fa-check-circle me-1"></i> Active Bills
            </button>
            <button
              className={`tab-btn btn btn-${activeTab === "cancelled" ? "danger" : "outline-danger"}`}
              onClick={() => setActiveTab("cancelled")}
            >
              <i className="fas fa-times-circle me-1"></i> Cancelled Bills
            </button>
          </div>
          {/* Filter Row 1 */}
          <div className="row g-2 mb-2">
            <div className="col-md-2">
              <label className="filter-label">Start Date</label>
              <input 
                type="date" 
                className="form-control filter-input-sm" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
              />
            </div>
            <div className="col-md-2">
              <label className="filter-label">End Date</label>
              <input 
                type="date" 
                className="form-control filter-input-sm" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
              />
            </div>
            <div className="col-md-2">
              <label className="filter-label">Payment Mode</label>
              <select 
                className="form-select filter-input-sm" 
                value={paymentMode} 
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <option value="">All</option>
                <option value='Cash'>Cash</option>
                <option value='Credit'>Credit</option>
                <option value='Bank Transfer'>Bank</option>
                <option value='Entertainment'>Entertainment</option>
                <option value='UPI'>UPI</option>
                <option value='QR Code'>QR Code</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="filter-label">Invoice No.</label>
              <input 
                type="text" 
                className="form-control filter-input-sm" 
                value={orderNoSearch} 
                onChange={(e) => setOrderNoSearch(e.target.value)} 
                placeholder="Search..."
              />
            </div>
            <div className="col-md-2">
              <label className="filter-label">Table</label>
              <input 
                type="text" 
                className="form-control filter-input-sm" 
                value={tableFilter} 
                onChange={(e) => setTableFilter(e.target.value)} 
                placeholder="Table no."
              />
            </div>
            <div className="col-md-2">
              <label className="filter-label">Sort By</label>
              <select 
                className="form-select filter-input-sm" 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Date & Time</option>
                <option value="amount">Amount</option>
                <option value="table">Table</option>
                <option value="invoice">Invoice</option>
              </select>
            </div>
          </div>
          
          {/* Filter Row 2 */}
          <div className="row g-2 mb-3">
            <div className="col-md-2">
              <label className="filter-label">Min Amount</label>
              <input 
                type="number" 
                className="form-control filter-input-sm" 
                value={amountFilter.min} 
                onChange={(e) => setAmountFilter(prev => ({ ...prev, min: e.target.value }))} 
                placeholder="Min ฿"
              />
            </div>
            <div className="col-md-2">
              <label className="filter-label">Max Amount</label>
              <input 
                type="number" 
                className="form-control filter-input-sm" 
                value={amountFilter.max} 
                onChange={(e) => setAmountFilter(prev => ({ ...prev, max: e.target.value }))} 
                placeholder="Max ฿"
              />
            </div>
            <div className="col-md-2">
              <label className="filter-label">Sort Order</label>
              <select 
                className="form-select filter-input-sm" 
                value={sortOrder} 
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
            <div className="col-md-6 d-flex align-items-end" style={{ gap: '8px' }}>
              <button className="btn btn-warning compact-btn" onClick={clearFilters}>
                <i className="fas fa-redo me-1"></i> Clear
              </button>
              <CSVLink
                data={filteredData.length > 0 ? filteredData : data}
                filename="salereport.csv"
                className="btn btn-success compact-btn text-decoration-none"
              >
                <i className="fas fa-file-csv me-1"></i> CSV
              </CSVLink>
              <button className="btn btn-danger compact-btn" onClick={exportPDF}>
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
              <div className="stats-badge ms-auto">
                <i className="fas fa-list me-1"></i> {filteredData.length} / {data.length} Bills
              </div>
            </div>
          </div>
        </div>
      </CardComponent>

      <div className="row">
        <div className="col-12">
          {filteredData.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">No data available.</p>
            </div>
          ) : (
            <CardComponent>
              <div className="table-responsive">
                <table className="table table-hover table-bordered" style={{ fontSize: '13px', marginBottom: 0 }}>
                  <thead style={{ 
                    background: 'linear-gradient(135deg, #ececf0ff 0%, #ffffffff 100%)', 
                    color: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    <tr>
                      <th className="text-center" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Inv. No.</th>
                      <th className="text-center" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Inv Date</th>
                      <th className="text-center" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Setup Date</th>
                      <th className="text-center" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Time</th>
                      <th className="text-right" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Subtotal</th>
                      <th className="text-right" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Tax</th>
                      <th className="text-right" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Grand Total</th>
                      <th className="text-center" style={{ padding: '12px 8px', fontWeight: '600', whiteSpace: 'nowrap' }}>Payment</th>
                      <th className="text-center" style={{ padding: '12px 8px', fontWeight: '600', minWidth: '200px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row, index) => (
                      <tr key={row.id} style={{ 
                        backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#f8f9fa' : 'white'}
                      >
                        <td className="text-center" style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                          <button 
                            className="btn btn-link p-0" 
                            onClick={() => printBillVATFormat(row)}
                            style={{ 
                              fontWeight: '600', 
                              textDecoration: 'none',
                              color: '#667eea'
                            }}
                          >
                            #{row.id}
                          </button>
                        </td>
                        <td className="text-center" style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {row.inv_date || '-'}
                        </td>
                        <td className="text-center" style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {row.setup_date || '-'}
                        </td>
                        <td className="text-center" style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          {row.inv_time || '-'}
                        </td>
                        <td className="text-right" style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          ฿ {parseFloat(row.subtotal_afterdiscount || 0).toFixed(2)}
                        </td>
                        <td className="text-right" style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          ฿ {parseFloat(row.tax || 0).toFixed(2)}
                        </td>
                        <td className="text-right" style={{ padding: '10px 8px', verticalAlign: 'middle', whiteSpace: 'nowrap', fontWeight: '600', color: '#2e7d32' }}>
                          ฿ {parseFloat(row.grand_total || 0).toFixed(2)}
                        </td>
                        <td className="text-center" style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                          <span className={`badge ${
                            row.payment_mode === 'Cash' ? 'bg-success' :
                            row.payment_mode === 'Credit' ? 'bg-warning' :
                            row.payment_mode === 'Bank Transfer' ? 'bg-info' :
                            'bg-secondary'
                          }`} style={{ fontSize: '11px', padding: '5px 10px' }}>
                            {row.payment_mode || 'N/A'}
                          </span>
                        </td>
                        <td className="text-center" style={{ padding: '10px 8px', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                            <button 
                              className="btn btn-sm btn-info" 
                              onClick={() => handleViewItems(row)} 
                              style={{ 
                                whiteSpace: 'nowrap',
                                fontSize: '12px',
                                padding: '5px 12px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                            <button 
                              className="btn btn-sm btn-success" 
                              onClick={() => printVATInvoice(row)} 
                              title="Print A4 VAT Format" 
                              style={{ 
                                whiteSpace: 'nowrap',
                                fontSize: '12px',
                                padding: '5px 12px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <i className="fas fa-print"></i> Print
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardComponent>
          )}
          <div className="mt-3">
          
            <h5>
              {activeTab === "cancelled" ? (
                <>Cancelled Bill Total: ฿{filteredData.reduce((acc, item) => acc + parseFloat(item.grand_total || 0), 0).toFixed(2)}</>
              ) : (
                <>Total Sale: ฿{filteredData.reduce((acc, item) => acc + parseFloat(item.grand_total || 0), 0).toFixed(2)}</>
              )}
            </h5>
          </div>
        </div>
      </div>

      <div className="row">
        <CardComponent>
          <div className="mt-4">
            <h5>Monthly Sales Summary</h5>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total" fill="#2334d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardComponent>
      </div>

      {showModal && (
        <BillItemModal
          isOpen={showModal}
          bill={selectedBill}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );

  // Main return statement with conditional rendering
  return isCashier ? (
    <div style={{ 
      width: '100vw', 
      height: '100vh', 
      backgroundColor: '#f8f9fa',
      overflow: 'auto'
    }}>
      <div style={{ padding: isCashier ? '0' : '20px' }}>
        {renderContent()}
      </div>
    </div>
  ) : (
    <Layout>
      <Header title="Sale Report" />
      {renderContent()}
    </Layout>
  );
}
